import { Combobox, Transition } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'
import React, { Fragment, useState } from 'react'
import { Controller, useFieldArray } from 'react-hook-form'

interface AdminManageChartCandidateNamesProps {
  electionType?: string
  labelText: string
  name: string
  length: number
  bgColor?: string
  showCandidateNames?: boolean
  control: object
  index: number
  partyIndex?: number
  manageState?: any
}

const RangeCombobox: React.FC<AdminManageChartCandidateNamesProps> = ({
  electionType,
  labelText,
  name,
  length,
  bgColor = '',
  showCandidateNames,
  index,
  partyIndex,
  manageState,
}) => {
  const {
    // @ts-ignore
    form: { control, getValues, setValue },
  } = manageState
  const { fields, append } = useFieldArray({
    control,
    name: `races.${index}.candidateNames`,
  })

  const bg = bgColor === 'white' ? `bg-[#FFFFFF]` : 'bg-[#E6EBF5]'
  const [query, setQuery] = useState('')
  const values = Array.from({ length: length }, (_, i) => i)
  const filteredValues =
    query === ''
      ? values
      : values.filter((val) => {
          return +val === +query
        })

  return (
    <>
      <div className="w-[2px] bg-[#E6EBF5] h-[48px]" />
      <div className="flex flex-col w-[84px]">
        <Controller
          control={control}
          // @ts-ignore
          name={name}
          render={({ field: { value, onChange } }) => (
            <Combobox
              value={value}
              onChange={(val) => {
                if (
                  labelText !== 'Write-Ins' &&
                  labelText !== 'Vote For' &&
                  showCandidateNames
                ) {
                  append({ name: '', order: fields.length })
                  if (setValue && getValues && labelText === 'Candidates') {
                    if (electionType === 'presidential') {
                      setValue(`races.0.politicalParties.0.candidates`, val)
                    } else {
                      setValue(`races.${index}.candidates`, val)
                    }
                  } else {
                    if (
                      (partyIndex || partyIndex === 0) &&
                      getValues().races[index].raceType === 'Partisan'
                    ) {
                      setValue(
                        `races.${index}.parties.${partyIndex}.candidates`,
                        val
                      )
                    } else {
                      setValue(`races.${index}.candidates`, val)
                    }
                  }
                } else {
                  onChange(val)
                }
              }}
              disabled={showCandidateNames}
            >
              <div
                className={`disabled:opacity-25 cursor-not-allowed relative h-[48px] overflow-visible`}
              >
                <div
                  className={`relative w-full h-full py-3 px-4 ${bg} rounded-md text-left cursor-default`}
                >
                  <Combobox.Input
                    className={`w-16 focus:outline-none text-lg py-1 pl-3 pr-5 ${bg} text-sm leading-5 text-gray-900 focus:ring-0 disabled:opacity-25 disabled:cursor-not-allowed`}
                    displayValue={(val) => {
                      return `${val ?? 0}`
                    }}
                    type="number"
                    onChange={(event) => setQuery(event.target.value)}
                    onFocus={(event: any) => event.target.select()}
                    disabled={showCandidateNames}
                  />
                  <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2 disabled:cursor-not-allowed disabled:opacity-25">
                    <ChevronUpDownIcon
                      className="w-5 h-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </Combobox.Button>
                </div>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  afterLeave={() => setQuery('')}
                >
                  <Combobox.Options className="absolute z-10 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                    {filteredValues.length === 0 && query !== '' ? (
                      <div className="relative px-4 py-2 text-gray-700 cursor-default select-none">
                        Nothing found.
                      </div>
                    ) : (
                      filteredValues.map((value: any) => (
                        <Combobox.Option
                          key={value}
                          className={({ active }) =>
                            `relative cursor-default select-none py-2 pl-6 pr-4 ${
                              active
                                ? 'text-blue-900 bg-blue-100'
                                : 'text-gray-900'
                            }`
                          }
                          value={value}
                        >
                          {({ selected, active }) => (
                            <>
                              <span
                                className={`block truncate ${
                                  selected ? 'font-medium' : 'font-normal'
                                }`}
                              >
                                {value}
                              </span>
                              {selected ? (
                                <span
                                  className={`absolute inset-y-0 left-0 flex items-center pl-1 ${
                                    active ? 'text-white' : 'text-teal-600'
                                  }`}
                                >
                                  <CheckIcon
                                    className="w-5 h-5"
                                    aria-hidden="true"
                                  />
                                </span>
                              ) : null}
                            </>
                          )}
                        </Combobox.Option>
                      ))
                    )}
                  </Combobox.Options>
                </Transition>
              </div>
            </Combobox>
          )}
        />
        <label
          htmlFor="writeIns"
          className="text-[#757575] font-light text-sm mt-1"
        >
          {labelText}
        </label>
      </div>
    </>
  )
}

export default RangeCombobox
